import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Grid from '@mui/material/Grid2';
import DonutChart from '@ui/components/DonutChart/DonutChart';
import { useTranslation } from 'react-i18next';
import { alpha, Box, Button, Typography, useTheme } from '@mui/material';
import AppAlert from '@ui/components/common/Alert';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { fetchFamilyFestDashboardCharts } from '@application/features/familyFest/familyFestDashboardChartsThunk';
import CapacityProgressBar from '@ui/components/CapacityProgressBar';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import IncreaseEventSlotsCapacityDialog from './IncreaseEventSlotsCapacityDialog';
import CompanyAttendeesCard from './CompanyAttendeesCard';
import OtherCompanyAttendeesGrid from './OtherCompanyAttendeesGrid';
import { updateFamilyFestEventSpotsAsync } from '@application/features/familyFest/familyFestEventSpotsThunk';
import { APIStatus } from '@application/enums/api-status';
import { fetchFamilyFestCompanyAttendeesAsync } from '@application/features/familyFest/familyFestCompanyAttendeesThunk';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
const FamilyFestDashboardTab = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { data } = useAppSelector((state) => state.familyFestDashboardCharts);
    const { status: spotsStatus } = useAppSelector((state) => state.familyFestEventSpots);
    const { companies } = useAppSelector((state) => state.familyFestCompanyAttendees);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [increaseSlotsDialogOpen, setIncreaseSlotsDialogOpen] = useState(false);
    // Legend items toggled off per chart title, as indices into that chart's non-zero segments.
    const [hiddenIndicesByChart, setHiddenIndicesByChart] = useState({});
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const { VDKEvents, VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const currentEvent = VDKEventsForEventDetails || (VDKEvents ?? []).find((e) => e.id === eventId);
    const isFamilyEvent = currentEvent?.type === EventTypeEnum.Family;
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const isTransportManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('TransportManager');
    const isHappinessManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('HappinessManager');
    const totalSpots = data?.totalSpots ?? 0;
    const assignedSpots = data?.assignedSpots ?? 0;
    // Consumed capacity = registered + spots locked by active waiting-list invitations.
    const reservedSpots = data?.reservedSpots ?? 0;
    const consumedSpots = assignedSpots + reservedSpots;
    const capacityPercent = useMemo(() => {
        if (totalSpots <= 0)
            return 0;
        return Math.min(100, parseFloat(((consumedSpots / totalSpots) * 100).toFixed(2)));
    }, [consumedSpots, totalSpots]);
    useEffect(() => {
        if (eventId) {
            dispatch(fetchFamilyFestDashboardCharts(eventId));
            dispatch(fetchFamilyFestCompanyAttendeesAsync(eventId));
        }
    }, [eventId, dispatch, selectedCompanyIds]);
    const totalCompanyAttendees = useMemo(() => companies.reduce((sum, c) => sum + c.totalAttendees, 0), [companies]);
    const topCompanies = companies.slice(0, 4);
    const cardColors = [
        theme.palette.success.main,
        theme.palette.primary.main,
        theme.palette.grey[500],
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.error.main,
        theme.palette.secondary.main
    ];
    // Fixed colour per invitation status, so a status looks the same in every status donut
    // (matches the Responses grid chips where they have a colour).
    const statusColors = {
        Registered: theme.palette.success.main,
        InProgress: theme.palette.primary.main,
        Invited: theme.palette.warning.main,
        NotComing: theme.palette.error.main,
        Waiting: theme.palette.grey[500],
        Pending: theme.palette.grey[300]
    };
    const renderInfoTooltip = (tooltipKey) => (_jsx(Box, { sx: { whiteSpace: 'pre-line' }, children: t(`events.eventDetails.familyFestDashboard.info.${tooltipKey}`) }));
    const chartConfigs = [
        {
            title: t('events.eventDetails.familyFestDashboard.ageGroups'),
            chart: data?.ageGroups,
            infoTooltip: renderInfoTooltip('ageGroups')
        },
        {
            title: t('events.eventDetails.familyFestDashboard.comingWith'),
            chart: data?.comingWith,
            infoTooltip: renderInfoTooltip('comingWith')
        },
        {
            title: t('events.eventDetails.familyFestDashboard.travelingToEvent'),
            chart: data?.travelMode,
            infoTooltip: renderInfoTooltip('travelingToEvent')
        },
        {
            title: t('events.eventDetails.familyFestDashboard.attendanceStatus'),
            chart: data?.comingToEvent,
            infoTooltip: renderInfoTooltip('attendanceStatus')
        },
        // Signup-source charts are Happiness Manager only.
        ...(isHappinessManager
            ? [
                {
                    title: t('events.eventDetails.familyFestDashboard.waitingListStatus'),
                    chart: data?.waitingList,
                    infoTooltip: renderInfoTooltip('waitingListStatus'),
                    colorByLabel: statusColors,
                    totalPersons: data?.waitingListTotalPersons
                },
                {
                    title: t('events.eventDetails.familyFestDashboard.protectedSignupStatus'),
                    chart: data?.protectedSignup,
                    infoTooltip: renderInfoTooltip('protectedSignupStatus'),
                    colorByLabel: statusColors,
                    totalPersons: data?.protectedSignupTotalPersons
                }
            ]
            : [])
    ];
    // Segments the donut shows: zero-value labels are left out.
    const getVisibleSegments = (chart) => (chart?.labels ?? [])
        .map((label, idx) => ({ label, value: chart.values[idx], persons: chart.persons?.[idx] }))
        .filter(({ value }) => value);
    // With legend items toggled off, count persons of the visible statuses only.
    const getHeaderPersons = (title, chart, total) => {
        const hidden = hiddenIndicesByChart[title] ?? [];
        if (!hidden.length || !chart?.persons)
            return total;
        return getVisibleSegments(chart).reduce((sum, { persons }, idx) => (hidden.includes(idx) ? sum : sum + (persons ?? 0)), 0);
    };
    const labelKeyMap = {
        // Age Groups
        '0-9': 'ageGroup0to9',
        '10-16': 'ageGroup10to16',
        '17-30': 'ageGroup17to30',
        '30+': 'ageGroup30Plus',
        // Coming With
        Family: 'family',
        Guest: 'guest',
        Alone: 'alone',
        // Travel Mode
        Bus: 'bus',
        Auto: 'autoCar',
        Plane: 'plane',
        // Attendance Status
        ComingTo: 'comingTo',
        CantMakeit: 'cantMakeIt',
        NotInterested: 'notInterested',
        Others: 'others',
        NoLongerEmployed: 'noLongerEmployed',
        // Waiting List Status
        Waiting: 'waitlistWaiting',
        Invited: 'waitlistInvited',
        InProgress: 'waitlistInProgress',
        Registered: 'waitlistRegistered',
        NotComing: 'waitlistNotComing',
        Pending: 'waitlistPending'
    };
    return (_jsxs(Box, { children: [_jsxs(Grid, { container: true, spacing: 2.5, height: 'calc(100dvh - 150px)', overflow: 'auto', children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Box, { padding: theme.spacing(2), borderRadius: 2, boxShadow: theme.customShadows.card, bgcolor: theme.palette.common.white, display: 'flex', flexDirection: 'column', gap: '3px', children: [_jsx(CapacityProgressBar, { label: t('events.eventDetails.transportTab.capacity'), current: consumedSpots, max: totalSpots, infoTooltip: renderInfoTooltip('capacityBar') }), _jsx(Box, { children: _jsx(AppAlert, { severity: 'info', variant: 'outlined', sx: {
                                            backgroundColor: alpha(theme.palette.grey[500], 0.08),
                                            border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                                            p: '6px',
                                            alignItems: 'flex-start',
                                            flexWrap: 'wrap',
                                            [theme.breakpoints.up('md')]: {
                                                flexWrap: 'nowrap',
                                                alignItems: 'center'
                                            },
                                            '& .MuiAlert-icon': {
                                                alignItems: 'flex-start',
                                                alignSelf: 'flex-start',
                                                p: 0,
                                                mr: 1,
                                                mt: '2px',
                                                [theme.breakpoints.up('md')]: {
                                                    alignItems: 'center',
                                                    alignSelf: 'center',
                                                    mt: 0
                                                }
                                            },
                                            '& .MuiAlert-message': {
                                                p: 0,
                                                flex: 1,
                                                minWidth: 0,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                [theme.breakpoints.up('md')]: {
                                                    alignItems: 'center'
                                                }
                                            },
                                            '& .MuiAlert-action': {
                                                p: 0,
                                                m: 0,
                                                alignItems: 'flex-start',
                                                alignSelf: 'center',
                                                [theme.breakpoints.down('md')]: {
                                                    flex: '1 0 100%',
                                                    width: '100%',
                                                    pl: '28px',
                                                    pt: 1,
                                                    ml: 0,
                                                    alignSelf: 'flex-start'
                                                }
                                            }
                                        }, icon: _jsx(Icon, { icon: 'eva:info-fill', color: theme.palette.text.primary }), action: isFamilyEvent && !isSfeermeester && !isTransportManager && (_jsx(Button, { variant: 'contained', size: 'small', color: 'inherit', onClick: () => setIncreaseSlotsDialogOpen(true), disabled: isTransportManager, children: t('events.eventDetails.familyFestDashboard.increaseEventSlots') })), children: _jsxs(Typography, { variant: 'body2', color: theme.palette.text.secondary, component: 'span', children: [t('events.eventDetails.familyFestDashboard.capacityAlertMessage', {
                                                    registered: assignedSpots.toLocaleString(i18n.language === 'de' ? 'de-DE' : i18n.language === 'nl' ? 'nl-NL' : 'en-US'),
                                                    percent: capacityPercent
                                                }), reservedSpots > 0 &&
                                                    ` ${t('events.eventDetails.familyFestDashboard.capacityLockedMessage', {
                                                        locked: reservedSpots.toLocaleString(i18n.language === 'de' ? 'de-DE' : i18n.language === 'nl' ? 'nl-NL' : 'en-US')
                                                    })}`] }) }) })] }) }), chartConfigs.map((item) => (_jsx(Grid, { size: { xs: 12, sm: 6, md: 4 }, children: _jsxs(Box, { sx: {
                                backgroundColor: '#F9FAFB',
                                border: '1px solid #919EAB33',
                                borderRadius: theme.spacing(4),
                                padding: theme.spacing(2.5),
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing(2),
                                height: '100%'
                            }, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1
                                    }, children: [_jsxs(Typography, { variant: 'h6', children: [item.title, 'totalPersons' in item && item.totalPersons != null && (_jsxs(Typography, { component: 'span', variant: 'body2', color: 'text.secondary', ml: 1, children: ["(", t('events.eventDetails.familyFestDashboard.totalPersons', {
                                                            count: getHeaderPersons(item.title, item.chart, item.totalPersons)
                                                        }), ")"] }))] }), item.infoTooltip && (_jsx(InfoIconTooltip, { title: item.infoTooltip, size: 'large', variant: 'dark', icon: _jsx(Icon, { icon: 'hugeicons:information-circle', color: theme.palette.action.active, fontSize: 20, style: { pointerEvents: 'none' } }) }))] }), _jsx(Box, { sx: {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden'
                                    }, children: _jsx(Box, { sx: { width: '100%', height: '100%', maxHeight: '250px' }, children: _jsx(DonutChart, { data: (() => {
                                                const filtered = getVisibleSegments(item.chart);
                                                return {
                                                    labels: filtered.map(({ label }) => t(`events.eventDetails.familyFestDashboard.${labelKeyMap[label] || label}`)),
                                                    values: filtered.map(({ value }) => value),
                                                    colors: filtered.map(({ label }, i) => ('colorByLabel' in item && item.colorByLabel?.[label]) ||
                                                        cardColors[i % cardColors.length])
                                                };
                                            })(), centerText: item.chart?.total ? item.chart.total.toString() : undefined, ringThickness: '85%', height: 200, onHiddenIndicesChange: (hidden) => setHiddenIndicesByChart((prev) => ({ ...prev, [item.title]: hidden })) }) }) })] }) }, item.title))), _jsx(Grid, { size: { xs: 12 }, children: _jsx(Box, { children: _jsx(Typography, { variant: 'h4', children: t('events.eventDetails.familyFestDashboard.topCompanyAttendees') }) }) }), topCompanies.map((company) => {
                        const percent = totalCompanyAttendees > 0
                            ? parseFloat(((company.totalAttendees / totalCompanyAttendees) * 100).toFixed(2))
                            : 0;
                        return (_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(CompanyAttendeesCard, { companyName: company.companyName, logoSrc: company.logoUrl ?? '', logoAlt: company.companyName, percent: percent, attendeeCount: company.totalAttendees, attendeesLabel: t('events.eventDetails.familyFestDashboard.attendees', 'attendees') }) }, company.companyId));
                    }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(OtherCompanyAttendeesGrid, {}) })] }), _jsx(IncreaseEventSlotsCapacityDialog, { open: increaseSlotsDialogOpen, onClose: () => setIncreaseSlotsDialogOpen(false), currentCapacity: totalSpots, isLoading: spotsStatus === APIStatus.PENDING, onUpdateCapacity: async (slotsToAdd) => {
                    const result = await dispatch(updateFamilyFestEventSpotsAsync({ eventId: eventId, slotsToAdd }));
                    if (updateFamilyFestEventSpotsAsync.fulfilled.match(result)) {
                        setIncreaseSlotsDialogOpen(false);
                        dispatch(fetchFamilyFestDashboardCharts(eventId));
                        toast.success(t('events.eventDetails.familyFestDashboard.capacityUpdatedSuccess'));
                    }
                    else {
                        toast.error(result.payload?.error ?? t('common.errorOccurred'));
                    }
                } })] }));
};
export default FamilyFestDashboardTab;
